/**
 * ----------------------------------------------------------------------------
 * SubContractorChart Component
 * ----------------------------------------------------------------------------
 * Summary:
 * Component for rendering nonconformity counts grouped by subcontractor as a
 * responsive bar chart. Fetches the subcontractor list and chart data based
 * on the selected date range and checked subcontractor filters, then displays
 * the results with gradient bar styling, value labels, and tooltips. A
 * collapsible, searchable subcontractor panel sits beside the chart.
 *
 * @author Chandan Singh
 * @created September-2026
 * ----------------------------------------------------------------------------
 */
import { observer } from "mobx-react"
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts"
import "./nc-bar-chart.css"
import { useEffect, useState } from "react"
import { useStore } from "../../../contexts/store-provider"
import { FormattedMessage } from "react-intl"
import noMessageIcon from '../../../assets/Images/no-noconfirmitry.png'
import { formatMessage } from "../../../translations/format-message"
import { CustomXAxisTick } from "./nc-chart-label"

interface ChartProps {
  fromDate: string
  toDate: string
}

function SubContractorChart(props: ChartProps) {
  const { fromDate, toDate } = props

  const {
    nonconformityStore
  } = useStore()

  const {
    GetSubContractorListService,
    getSubContractorCheckedValues,
    GetBySubContractorReport,
    subContractorChartData,
    subContractorList,
    UpdateColumnSelection,
    getNonconformitySummaryDetails
  } = nonconformityStore

  const [isListLoaded, setIsListLoaded] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false)
  useEffect(() => {
    const fetchList = async () => {
      await GetSubContractorListService()
      setIsListLoaded(true)
    }
    fetchList()
  }, [fromDate, toDate])

  useEffect(() => {
    if (!isListLoaded) return;
    if (!getSubContractorCheckedValues || !getSubContractorCheckedValues.length) return;

    GetBySubContractorReport(fromDate, toDate, getSubContractorCheckedValues)
  }, [isListLoaded, getSubContractorCheckedValues])

  // Subcontractors are identified by name in both the filter and the report.
  const allNames: string[] = (subContractorList ?? []).map((s: any) => s.SubContractorName)

  const allChecked =
    allNames.length > 0 && getSubContractorCheckedValues.length === allNames.length

  const filteredNames = allNames.filter((name) =>
    name.toLowerCase().includes(searchText.trim().toLowerCase())
  )

  const toggleSubContractor = (name: string) => {
    const next = getSubContractorCheckedValues.includes(name)
      ? getSubContractorCheckedValues.filter((x) => x !== name)
      : // keep the original list order
      allNames.filter((x) => x === name || getSubContractorCheckedValues.includes(x))
    UpdateColumnSelection(next)
  }

  const toggleAll = () => {
    UpdateColumnSelection(allChecked ? [] : allNames)
  }

  // Totals come from the chart data, so only checked subcontractors have a count.
  const getSubContractorTotal = (name: string): number | undefined => {
    const row = subContractorChartData?.ChartReportList?.find(
      (r: any) => r.DisplayName === name
    )
    return row?.TotalCount
  }

  return (
    <div className="nc-process-view">
      <div className="nc-stat-row">
        {getNonconformitySummaryDetails.map((card) => (
          <div key={card.labelId} className={`nc-stat-card ${card.variant}`}>
            <div className="nc-stat-label">
              <FormattedMessage id={card.labelId} />
            </div>
            <div className="nc-stat-value">
              {card.value}
              <span className="nc-stat-unit">
                <FormattedMessage id="nc_requests" />
              </span>
            </div>
            <div className="nc-stat-progress">
              {/* Total card shows a plain line; the hidden percent keeps its height equal to the other cards. */}
              <div className="nc-stat-track" style={card.labelId === "nc_total_trays" ? { height: 4, background: "#fff", marginTop: "7px" } : undefined}>
                {card.labelId !== "nc_total_trays" && (
                  <div className="nc-stat-fill" style={{ width: `${card.percent}%` }} />
                )}
              </div>
              {card.labelId !== "nc_total_trays" &&
                <span className="nc-stat-percent">{card.percent}%</span>
              }
            </div>
          </div>
        ))}
      </div>

      <div className={`nc-chart-row${isPanelCollapsed ? " nc-panel-collapsed" : ""}`}>
        <div className="nc-chart-container nc-chart-subcontractor nc-chart-main">
          {!isListLoaded ? null : !getSubContractorCheckedValues.length ? (
            <div className="message">
              <div className="d-flex message-area">
                <div className="no-data-img">
                  <img
                    src={noMessageIcon}
                    alt=""
                    className="no_message"
                  />
                </div>
                <div>
                  <h3 className="fw-bold display-6">
                    {' '}
                    <FormattedMessage id="no_column_selected" /><br />
                    <FormattedMessage id="please_select_a_column" />
                  </h3>
                </div>
              </div>
            </div>
          ) :
            !subContractorChartData || !subContractorChartData?.ChartReportList?.length ? (
              <div className="message">
                <div className="d-flex message-area">
                  <div className="no-data-img">
                    <img
                      src={noMessageIcon}
                      alt=""
                      className="no_message"
                    />
                  </div>
                  <div>
                    <h3 className="fw-bold display-6">
                      {' '}
                      <FormattedMessage id="no_nonconformity" />
                    </h3>
                  </div>
                </div>
              </div>
            ) : (

              <ResponsiveContainer width="100%" height="100%" className="nc-chart-canvas">
                <BarChart data={subContractorChartData.ChartReportList} margin={{ top: 24, right: 24, left: 0, bottom: 8 }} barCategoryGap="30%">
                  <defs>
                    <linearGradient id="ncBarSubcontractorGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" className="nc-bar-gradient-start" />
                      <stop offset="100%" className="nc-bar-gradient-end" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="DisplayName" className="nc-chart-axis-x" axisLine={false} tickLine={false} angle={45} textAnchor="start" tickMargin={3} height={100} tick={<CustomXAxisTick />} />
                  <YAxis allowDecimals={false} className="nc-chart-axis-y" axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ className: "nc-chart-cursor" }} formatter={(value) => [`${value}`,formatMessage("total_count")]}/>
                  <Bar
                    dataKey="TotalCount"
                    fill="var(--nc-bar-tint)"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={64}
                    className="nc-bar-rect"
                    activeBar={{ fill: "url(#ncBarSubcontractorGradient)", className: "nc-bar-rect nc-bar-active" }}
                  >
                    <LabelList dataKey="TotalCount" position="top" className="nc-chart-label" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
        </div>

        {isPanelCollapsed && (
          <button
            type="button"
            className="nc-legend-strip"
            onClick={() => setIsPanelCollapsed(false)}
            aria-label="Expand sub contractor panel"
            title="Expand"
          >
            <span className="nc-legend-strip-icon">&laquo;</span>
            <span className="nc-legend-strip-text">
              <FormattedMessage id="sub_contractor_name" />
            </span>
          </button>
        )}

        <aside className="nc-legend-panel">
          <div className="nc-legend-title">
            <FormattedMessage id="sub_contractor_name" />
            <button
              type="button"
              className="nc-legend-toggle"
              onClick={() => setIsPanelCollapsed(true)}
              aria-label="Collapse sub contractor panel"
              title="Collapse"
            >
              &raquo;
            </button>
          </div>
          <div className="nc-legend-body">
            <div className="nc-legend-all">
              <label className="nc-legend-item">
                <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                <span><FormattedMessage id="select_all_options" /></span>
              </label>
              <div className="nc-legend-search-wrap">
                <input
                  type="text"
                  className="nc-legend-search"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={formatMessage("search")}
                />
                {searchText && (
                  <button
                    type="button"
                    className="nc-legend-search-clear"
                    onClick={() => setSearchText("")}
                    aria-label="Clear search"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            <div className="nc-legend-list">
              {filteredNames.map((name) => (
                <label key={name} className="nc-legend-item">
                  <input
                    type="checkbox"
                    checked={getSubContractorCheckedValues.includes(name)}
                    onChange={() => toggleSubContractor(name)}
                  />
                  <span className="nc-legend-name">{name}</span>
                  <span className="nc-legend-count">
                    {getSubContractorCheckedValues.includes(name)
                      ? getSubContractorTotal(name) ?? 0
                      : null}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default observer(SubContractorChart)
